import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Empty,
  Input,
  Space,
  Spin,
  Tag,
  Tooltip,
  Tree,
  message,
  theme,
  Typography,
} from 'antd'
import {
  MinusSquareOutlined,
  PlusSquareOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import type { TreeProps } from 'antd/es/tree'

import { fetchDatalensTree } from '@/api/datalens'
import {
  collectAllKeys,
  convertTreeNodes,
  entryIdFromKey,
  filterTree,
  findTitleByKey,
  isEntryKey,
  keyFromEntryId,
  nodeIcon,
  type TreeNodeData,
} from '@/utils/datalensTree'

const { Text } = Typography

const ORG_SCOPE_SOURCE_LABELS: Record<string, string> = {
  template_instance: 'template',
  access_group: 'access group',
}

export interface DatalensEntrySelection {
  datalens_entry_id: string
  entry_title: string | null
}

export interface DatalensEntryMarker {
  label: string
  color?: string
}

interface DatalensEntryPickerProps {
  value?: string | null
  values?: string[]
  mode?: 'single' | 'multiple'
  onChange?: (next: DatalensEntrySelection | null) => void
  onChangeMany?: (next: DatalensEntrySelection[]) => void
  orgId?: string | null
  disabled?: boolean
  disabledEntryIds?: Set<string>
  markersByEntryId?: Record<string, DatalensEntryMarker[]>
  height?: number
}

export default function DatalensEntryPicker({
  value,
  values = [],
  mode = 'single',
  onChange,
  onChangeMany,
  orgId,
  disabled = false,
  disabledEntryIds = new Set(),
  markersByEntryId = {},
  height = 360,
}: DatalensEntryPickerProps) {
  const { token } = theme.useToken()
  const [searchText, setSearchText] = useState('')
  const [treeData, setTreeData] = useState<TreeNodeData[]>([])
  const [treeLoading, setTreeLoading] = useState(true)
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const loadedTreeScopeRef = useRef<string | null>(null)

  const selectedKey = value ? keyFromEntryId(value) : undefined
  const checkedKeys = useMemo(() => values.map(keyFromEntryId), [values])

  const loadTree = useCallback(async (bust?: string) => {
    setTreeLoading(true)
    try {
      const resp = await fetchDatalensTree({
        ...(bust ? { bust } : {}),
        ...(orgId ? { org_id: orgId } : {}),
      })
      const nodes = convertTreeNodes(resp.tree)
      setTreeData(nodes)
      return nodes
    } catch (error) {
      console.error('Failed to load DataLens tree:', error)
      message.error('Ошибка загрузки дерева DataLens')
      return []
    } finally {
      setTreeLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    const scopeKey = orgId || 'all'
    if (loadedTreeScopeRef.current === scopeKey) return
    loadedTreeScopeRef.current = scopeKey
    loadTree()
  }, [loadTree, orgId])

  const displayTreeData = useMemo(() => filterTree(treeData, searchText), [treeData, searchText])
  const selectableTreeData = useMemo(() => {
    const markSelectable = (nodes: TreeNodeData[]): TreeNodeData[] =>
      nodes.map((node) => {
        const entryId = isEntryKey(String(node.key)) ? entryIdFromKey(String(node.key)) : null
        const markers = entryId ? markersByEntryId[entryId] || [] : []
        const title = typeof node.title === 'function' ? String(node.key) : node.title
        const showOrgScope = node.nodeType === 'dash' && node.orgScoped
        return {
          ...node,
          selectable: node.nodeType === 'dash' && !disabledEntryIds.has(entryId || ''),
          disableCheckbox: node.nodeType !== 'dash' || disabledEntryIds.has(entryId || ''),
          title: (
            <Space size={[6, 4]} wrap>
              <span>{title}</span>
              {showOrgScope ? (
                <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                  {node.orgIds.length > 1 ? `orgs ${node.orgIds.length}` : 'org scope'}
                </Tag>
              ) : null}
              {showOrgScope
                ? node.orgScopeSources.map((source) => (
                    <Tag key={`${node.key}-${source}`} color="blue" style={{ marginInlineEnd: 0 }}>
                      {ORG_SCOPE_SOURCE_LABELS[source] || source}
                    </Tag>
                  ))
                : null}
              {markers.map((marker) => (
                <Tag key={`${node.key}-${marker.label}`} color={marker.color} style={{ marginInlineEnd: 0 }}>
                  {marker.label}
                </Tag>
              ))}
              {entryId && disabledEntryIds.has(entryId) ? (
                <Tag color="default" style={{ marginInlineEnd: 0 }}>уже выбрано</Tag>
              ) : null}
            </Space>
          ),
          children: node.children ? markSelectable(node.children) : undefined,
        }
      })

    return markSelectable(displayTreeData)
  }, [disabledEntryIds, displayTreeData, markersByEntryId])

  useEffect(() => {
    if (searchText) {
      setExpandedKeys(collectAllKeys(selectableTreeData))
    } else if (selectedKey) {
      setExpandedKeys((prev) => (prev.includes(selectedKey) ? prev : prev))
    } else {
      setExpandedKeys([])
    }
  }, [searchText, selectableTreeData, selectedKey])

  const handleSelect: TreeProps['onSelect'] = (keys, info) => {
    if (mode === 'multiple') {
      return
    }
    const nextKey = String(keys[0] ?? '')
    const nodeType = (info.node as unknown as TreeNodeData).nodeType
    if (!nextKey || !isEntryKey(nextKey) || !info.node.isLeaf || nodeType !== 'dash') {
      return
    }
    onChange?.({
      datalens_entry_id: entryIdFromKey(nextKey),
      entry_title: findTitleByKey(treeData, nextKey),
    })
  }

  const handleCheck: TreeProps['onCheck'] = (keys) => {
    const rawKeys = Array.isArray(keys) ? keys : keys.checked
    const entries = rawKeys
      .map(String)
      .filter((key) => isEntryKey(key))
      .map((key) => ({
        datalens_entry_id: entryIdFromKey(key),
        entry_title: findTitleByKey(treeData, key),
      }))
      .filter((entry) => !disabledEntryIds.has(entry.datalens_entry_id))

    onChangeMany?.(entries)
  }

  const handleRefreshTree = useCallback(async () => {
    setExpandedKeys([])
    const nodes = await loadTree(String(Date.now()))
    if (nodes.length > 0) {
      message.success('Структура обновлена')
    }
  }, [loadTree])

  const selectedTitle = selectedKey ? findTitleByKey(treeData, selectedKey) : null

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Input
        allowClear
        disabled={disabled}
        prefix={<SearchOutlined />}
        placeholder="Поиск по дашбордам, воркбукам и коллекциям..."
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
      />

      <Space>
        <Tooltip title="Развернуть все">
          <Button
            size="small"
            disabled={disabled || treeLoading}
            icon={<PlusSquareOutlined />}
            onClick={() => setExpandedKeys(collectAllKeys(selectableTreeData))}
          />
        </Tooltip>
        <Tooltip title="Свернуть все">
          <Button
            size="small"
            disabled={disabled || treeLoading}
            icon={<MinusSquareOutlined />}
            onClick={() => setExpandedKeys([])}
          />
        </Tooltip>
        <Button
          size="small"
          disabled={disabled}
          icon={<ReloadOutlined />}
          loading={treeLoading}
          onClick={handleRefreshTree}
        >
          Обновить структуру
        </Button>
        <Button
          size="small"
          disabled={disabled || (mode === 'single' ? !selectedKey : values.length === 0)}
          onClick={() => {
            if (mode === 'multiple') {
              onChangeMany?.([])
              return
            }
            onChange?.(null)
          }}
        >
          Сбросить выбор
        </Button>
      </Space>

      <Alert
        type="info"
        showIcon
        message="Выберите дашборд в дереве BI-объектов"
        description={
          mode === 'multiple'
            ? `Можно выбрать сразу несколько дашбордов. Коллекции и воркбуки используются только для навигации.${orgId ? ' Дерево ограничено организацией выбранного пользователя или группы.' : ''}`
            : `Коллекции и воркбуки используются только для навигации.${orgId ? ' Дерево ограничено организацией выбранного пользователя или группы.' : ''}`
        }
      />

      {mode === 'single' && selectedKey ? (
        <Alert
          type="success"
          showIcon
          message={selectedTitle || value}
          description={<Text code>{value}</Text>}
        />
      ) : null}

      {treeLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin tip="Загрузка дерева DataLens..." />
        </div>
      ) : displayTreeData.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={searchText ? 'Ничего не найдено по запросу' : 'Дерево DataLens пусто'}
        />
      ) : (
        <Tree
          showIcon
          icon={nodeIcon}
          treeData={selectableTreeData}
          selectedKeys={mode === 'single' && selectedKey ? [selectedKey] : []}
          onSelect={handleSelect}
          checkable={mode === 'multiple'}
          checkedKeys={mode === 'multiple' ? checkedKeys : undefined}
          onCheck={handleCheck}
          expandedKeys={expandedKeys}
          onExpand={(keys) => setExpandedKeys(keys as string[])}
          height={height}
          style={{
            background: token.colorBgContainer,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadiusLG,
            padding: 12,
          }}
        />
      )}
    </Space>
  )
}
